const text = {
  en: {
    pageTitle: "Company Configuration",
    pageSubtitle:
      "Configure your company's attendance rules, overtime policies and system features",
    totalCompanies: "Total Companies",
    configuredCompanies: "Configured Companies",
    selectedCompanyLabel: "Selected Company",
    saveSuccess: "Configuration saved successfully!",
    saving: "Saving...",
    saveConfiguration: "Save Configuration",
    createConfiguration: "Create Config",
    creatingConfiguration: "Creating...",
    startCreateConfiguration: "Create New Config",
    confirmCreateConfiguration: "Create Config Now",
    cancelCreateConfiguration: "Cancel Create",
    effectiveDateBannerDefault: "Effective dates are locked. Use Create New Config to edit effective dates.",
    effectiveDateBannerCreate: "Create mode is active: effective dates are now editable.",
    resetToDefault: "Reset to Default",
    confirmReset:
      "Are you sure you want to reset all configurations to default?",
    detectCompanies: "Select Company",
    detectingCompanies: "Loading...",
    currentlyConfiguring: "Configuring:",
    noCompanySelected: "No company selected",

    companyModal: {
      title: "Select Company to Configure",
      subtitle: "Choose a company from the list below",
      employeeCount: "Employees",
      configured: "Configured",
      notConfigured: "Not Configured",
      noCompanies: "No companies detected for this client",
      autoCreate: "Auto-create Default Configurations",
      close: "Close",
      selectCompany: "Select this company",
    },

    tabs: {
      companyInfo: "Company Info",
      attendance: "Attendance",
      overtime: "Overtime",
      leaves: "Leaves",
      rewards: "Rewards",
      features: "Features",
      advancements: "Advancements",
      payrollDates: "Payroll Dates",
      fingerprint: "Fingerprint",
      notifications: "Notifications",
      workflows: "Workflows",
    },

    company: {
      title: "Company Information",
      companyName: "Company Name",
      industry: "Industry",
      email: "Email",
      phone: "Phone",
      address: "Address",
      website: "Website",
      notSet: "Not set",
    },

    attendance: {
      title: "Attendance & Working Hours",
      effectiveDate: "Effective Date",
      workStartTime: "Work Start Time",
      workEndTime: "Work End Time",
      isOvernightShift: "Overnight Shift (crosses midnight)",
      isOvernightShiftHelp: "Enable when the shift ends on the next calendar day, e.g. 14:00 → 02:00.",
      shiftDayPivotHour: "Day-Boundary Hour (advanced)",
      shiftDayPivotHourHelp: "Optional. Hour of day (0–23) when one shift-day ends and the next begins. Leave empty to auto-compute.",
      workingHoursPerDay: "Working Hours Per Day",
      workingDaysPerWeek: "Working Days Per Week",
      lateAbsenceRules: "Late & Absence Rules",
      absenceDeduction: "Absence Deduction Per Day",
      maxLatePerMonth: "Max Late Per Month",
      gracePeriods: "Grace Periods Per Month",
      noDeduction: "No deduction",
      quarterDay: "0.25 day",
      halfDay: "0.5 day",
      fullDay: "1 day",
      oneAndQuarterDay: "1.25 days",
      oneAndHalfDay: "1.5 days",
      twoDays: "2 days",
      lateDeductionRules: "Late Deduction Rules (By Minutes)",
      fromMinute: "From Minute",
      toMinute: "To Minute (leave empty for unlimited)",
      deductionPercentage: "Deduction % of Daily Rate",
      unlimited: "Unlimited",
      addRule: "Add Rule",
      removeRule: "Remove Last Rule",
      lateThresholdMinutes: "Late Threshold Minutes",
      additionalMinutes: "Additional Minutes to Add",
      defaultDailyRatePercentage: "Default Deduction % of Daily Rate",
      defaultDailyRatePercentageDesc: "Deduction percentage of the employee's daily rate applied when they check in but forget to check out (e.g. 0.5 = deduct half the daily rate). Set to 0 to disable.",
      halfDayRequestThreshold: "Half Day Request Threshold (Hours)",
      halfDayRequestThresholdDesc: "Minimum shift time change (in hours/decimal) required for early check-in/out requests to be considered Half Day Leave requests. For example: if set to 3, a request for 3+ hours difference will be treated as a Half Day Leave.",
    },

    overtime: {
      title: "Overtime Configuration",
      effectiveDate: "Effective Date",
      enableOvertime: "Enable Overtime",
      overtimeRate: "Overtime Rate (Multiplier)",
      overtimeStartsAfter: "Overtime Starts After (Hours)",
      maxOvertimePerDay: "Max Overtime Hours Per Day",
      approvalRequired: "Overtime Approval Required",
      notificationSettings: "Notification Settings",
      lateNotifications: "Late Notifications",
      absenceNotifications: "Absence Notifications",
      overtimeNotifications: "Overtime Notifications",
      leaveNotifications: "Leave Notifications",
    },

    leaves: {
      title: "Leave & Vacation Policies",
      effectiveDate: "Effective Date",
      progressiveLeave: "Progressive Annual Leave (Based on Service Years)",
      fromYear: "From Year",
      toYear: "To Year (leave empty for unlimited)",
      unlimited: "Unlimited",
      annualLeaveDays: "Annual Leave Days",
      addRule: "Add Rule",
      removeRule: "Remove Last Rule",
      otherLeaveTypes: "Other Leave Types",
      sickLeavePerYear: "Sick Leave Per Year (Days)",
      casualLeavePerYear: "Casual Leave Per Year (Days)",
      leaveApprovalRequired: "Leave Approval Required",
      carryOverLeaves: "Carry Over Leaves to Next Year",
    },

    advancements: {
      title: "Salary Advancements (Solfa)",
      effectiveDate: "Effective Date",
      enableAdvancements: "Enable Salary Advancements",
      minMonthsBeforeRequest: "Minimum Months Before Request",
      maxAdvancementPercentage: "Max Advancement (% of Salary)",
      maxAdvancementAmount: "Max Advancement Amount",
      maxActiveAdvancements: "Max Active Advancements",
      maxAdvancementsPerMonth: "Max Advancements Per Month",
      maxMonthlyAdvancementTotal: "Max Monthly Total Amount",
      minInstallments: "Min Installments",
      maxInstallments: "Max Installments",
      defaultInstallments: "Default Installments",
      autoApproveUnderAmount: "Auto-Approve Under Amount (EGP)",
      emergencyMaxAmount: "Emergency Max Amount (EGP)",
      processingDays: "Processing Days",
      deductionStartDelay: "Deduction Start Delay (Months)",
      requireManagerApproval: "Require Manager Approval",
      requireHRApproval: "Require HR Approval",
      allowMultipleRequests: "Allow Multiple Active Requests",
      allowEmergencyAdvancements: "Allow Emergency Advancements",
      blockIfOverdue: "Block if Overdue Repayments",
      advancementNotifications: "Advancement Notifications",
      repaymentMonths: "Repayment Period (Months)",
      interestRate: "Interest Rate (%)",
      requireApproval: "Require Manager Approval",
    },

    rewards: {
      title: "Rewards & Bonuses",
      performanceBonusEnabled: "Performance Bonus Enabled",
      attendanceBonusEnabled: "Attendance Bonus Enabled",
      monthlyBonusAmount: "Monthly Bonus Amount",
      attendanceBonusCriteria: "Attendance Bonus Criteria (%)",
    },

    features: {
      title: "System Features & Privacy",
      privateMessaging: "Private Messaging",
      documentSharing: "Document Sharing",
      performanceReviews: "Performance Reviews",
      salarySlips: "Salary Slips",
    },

    fingerprint: {
      title: "Fingerprint Configuration",
      detectionMode: "Attendance Detection Mode",
      detectionModeDesc: "How the system determines if a fingerprint event is check-in or check-out",
      deviceName: "Device Name",
      deviceNameDesc: "Uses the fingerprint device name to determine check-in/check-out. Requires separate devices for each.",
      sequential: "Sequential",
      sequentialDesc: "Determines check-in/check-out based on event sequence and shift timing. Works with a single device.",
      currentMode: "Current Mode",
    },
  },

  ar: {
    pageTitle: "إعدادات الشركة",
    pageSubtitle:
      "قم بتكوين قواعد الحضور وسياسات العمل الإضافي وميزات النظام الخاصة بشركتك",
    totalCompanies: "إجمالي الشركات",
    configuredCompanies: "الشركات المهيأة",
    selectedCompanyLabel: "الشركة المختارة",
    saveSuccess: "تم حفظ الإعدادات بنجاح!",
    saving: "جاري الحفظ...",
    saveConfiguration: "حفظ الإعدادات",
    createConfiguration: "إنشاء إعداد جديد",
    creatingConfiguration: "جاري الإنشاء...",
    startCreateConfiguration: "إنشاء إعداد جديد",
    confirmCreateConfiguration: "تأكيد إنشاء الإعداد",
    cancelCreateConfiguration: "إلغاء الإنشاء",
    effectiveDateBannerDefault: "تواريخ السريان مقفلة. استخدم إنشاء إعداد جديد لتعديلها.",
    effectiveDateBannerCreate: "وضع الإنشاء مفعل: تواريخ السريان قابلة للتعديل الآن.",
    resetToDefault: "إعادة التعيين للافتراضي",
    confirmReset:
      "هل أنت متأكد من إعادة تعيين جميع الإعدادات للقيم الافتراضية؟",
    detectCompanies: "اختر الشركة",
    detectingCompanies: "جاري التحميل...",
    currentlyConfiguring: "الشركة الحالية :",
    noCompanySelected: "لم يتم اختيار شركة",

    companyModal: {
      title: "اختر الشركة للتكوين",
      subtitle: "اختر شركة من القائمة أدناه",
      employeeCount: "الموظفون",
      configured: "تم التكوين",
      notConfigured: "لم يتم التكوين",
      noCompanies: "لم يتم اكتشاف شركات لهذا العميل",
      autoCreate: "إنشاء تلقائي للتكوينات الافتراضية",
      close: "إغلاق",
      selectCompany: "اختر هذه الشركة",
    },

    tabs: {
      companyInfo: "معلومات الشركة",
      attendance: "الحضور",
      overtime: "العمل الإضافي",
      leaves: "الإجازات",
      rewards: "المكافآت",
      features: "الميزات",
      advancements: "السلف",
      payrollDates: "تواريخ الراتب",
      fingerprint: "البصمة",
      notifications: "الإشعارات",
      workflows: "سير العمل",
    },

    company: {
      title: "معلومات الشركة",
      companyName: "اسم الشركة",
      industry: "المجال",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      address: "العنوان",
      website: "الموقع الإلكتروني",
      notSet: "غير محدد",
    },

    attendance: {
      title: "الحضور وساعات العمل",
      effectiveDate: "تاريخ السريان",
      workStartTime: "وقت بدء العمل",
      workEndTime: "وقت انتهاء العمل",
      isOvernightShift: "وردية ليلية (تمتد بعد منتصف الليل)",
      isOvernightShiftHelp: "فعّل هذا الخيار إذا كانت الوردية تنتهي في اليوم التالي، مثل 14:00 ← 02:00.",
      shiftDayPivotHour: "ساعة فاصل اليوم (متقدم)",
      shiftDayPivotHourHelp: "اختياري. ساعة من اليوم (0–23) تفصل بين الوردية وما بعدها. اتركه فارغًا للحساب التلقائي.",
      workingHoursPerDay: "ساعات العمل في اليوم",
      workingDaysPerWeek: "أيام العمل في الأسبوع",
      lateAbsenceRules: "قواعد التأخير والغياب",
      absenceDeduction: "خصم الغياب لكل يوم",
      maxLatePerMonth: "الحد الأقصى للتأخير في الشهر",
      gracePeriods: "فترات السماح في الشهر",
      noDeduction: "بدون خصم",
      quarterDay: "ربع يوم (0.25)",
      halfDay: "نصف يوم (0.5)",
      fullDay: "يوم كامل (1)",
      oneAndQuarterDay: "يوم وربع (1.25)",
      oneAndHalfDay: "يوم ونصف (1.5)",
      twoDays: "يومين (2)",
      lateDeductionRules: "قواعد خصم التأخير (بالدقائق)",
      fromMinute: "من الدقيقة",
      toMinute: "إلى الدقيقة (اترك فارغا لغير محدود)",
      deductionPercentage: "نسبة الخصم من الراتب اليومي (%)",
      unlimited: "غير محدود",
      addRule: "إضافة قاعدة",
      removeRule: "حذف آخر قاعدة",
      lateThresholdMinutes: "دقائق حد التأخير",
      additionalMinutes: "الدقائق الإضافية المراد إضافتها",
      defaultDailyRatePercentage: "نسبة الخصم الافتراضية من الراتب اليومي",
      defaultDailyRatePercentageDesc: "نسبة الخصم من الراتب اليومي للموظف عند تسجيل الحضور بدون تسجيل الخروج (مثال: 0.5 = خصم نصف اليومية). اضبطها على 0 لتعطيل الخصم.",
      halfDayRequestThreshold: "حد طلب نصف اليوم (بالساعات)",
      halfDayRequestThresholdDesc: "الحد الأدنى من التغيير في وقت العمل (بالساعات/الكسور العشرية) المطلوب لاعتبار طلبات الرحيل المبكر أو الحضور المبكر طلبات نصف يوم إجازة. على سبيل المثال: إذا تم تعيينه على 3، فسيتم التعامل مع طلب بفرق 3+ ساعات كطلب نصف يوم إجازة.",
    },

    overtime: {
      title: "إعدادات العمل الإضافي",
      effectiveDate: "تاريخ السريان",
      enableOvertime: "تفعيل العمل الإضافي",
      overtimeRate: "معدل العمل الإضافي (المضاعف)",
      overtimeStartsAfter: "يبدأ العمل الإضافي بعد (ساعات)",
      maxOvertimePerDay: "الحد الأقصى لساعات العمل الإضافي في اليوم",
      approvalRequired: "يتطلب موافقة على العمل الإضافي",
      notificationSettings: "إعدادات الإشعارات",
      lateNotifications: "إشعارات التأخير",
      absenceNotifications: "إشعارات الغياب",
      overtimeNotifications: "إشعارات العمل الإضافي",
      leaveNotifications: "إشعارات الإجازات",
    },

    advancements: {
      title: "السلف المالية (الصولفة)",
      effectiveDate: "تاريخ السريان",
      enableAdvancements: "تفعيل السلف المالية",
      minMonthsBeforeRequest: "الحد الأدنى للأشهر قبل الطلب",
      maxAdvancementPercentage: "الحد الأقصى للسلفة (% من الراتب)",
      maxAdvancementAmount: "الحد الأقصى لمبلغ السلفة",
      maxActiveAdvancements: "عدد السلف النشطة المسموح بها",
      maxAdvancementsPerMonth: "الحد الأقصى للسلف الشهرية",
      maxMonthlyAdvancementTotal: "الحد الأقصى للمبلغ الشهري",
      minInstallments: "الحد الأدنى للأقساط",
      maxInstallments: "الحد الأقصى للأقساط",
      defaultInstallments: "عدد الأقساط الافتراضي",
      autoApproveUnderAmount: "الموافقة الآلية تحت المبلغ (جنيه)",
      emergencyMaxAmount: "الحد الأقصى للسلفة الطارئة (جنيه)",
      processingDays: "عدد أيام المعالجة",
      deductionStartDelay: "تأخير بدء الخصم (شهور)",
      requireManagerApproval: "اشتراط موافقة المدير",
      requireHRApproval: "اشتراط موافقة الموارد البشرية",
      allowMultipleRequests: "السماح بطلبات متعددة نشطة",
      allowEmergencyAdvancements: "السماح بالسلف الطارئة",
      blockIfOverdue: "منع إذا كانت هناك أقساط متأخرة",
      advancementNotifications: "إشعارات السلفة",
      repaymentMonths: "فترة السداد (بالأشهر)",
      interestRate: "معدل الفائدة (%)",
      requireApproval: "يتطلب موافقة المدير",
    },

    leaves: {
      title: "سياسات الإجازات والعطلات",
      effectiveDate: "تاريخ السريان",
      progressiveLeave: "الإجازة السنوية التدريجية (حسب سنوات الخدمة)",
      fromYear: "من سنة",
      toYear: "إلى سنة (اتركه فارغاً لغير محدود)",
      unlimited: "غير محدود",
      annualLeaveDays: "أيام الإجازة السنوية",
      addRule: " إضافة قاعدة",
      removeRule: " حذف آخر قاعدة",
      otherLeaveTypes: "أنواع الإجازات الأخرى",
      sickLeavePerYear: "الإجازة المرضية في السنة (أيام)",
      casualLeavePerYear: "الإجازة العارضة في السنة (أيام)",
      leaveApprovalRequired: "يتطلب موافقة على الإجازة",
      carryOverLeaves: "نقل الإجازات المتبقية للسنة القادمة",
    },

    rewards: {
      title: "المكافآت والحوافز",
      performanceBonusEnabled: "تفعيل مكافأة الأداء",
      attendanceBonusEnabled: "تفعيل مكافأة الحضور",
      monthlyBonusAmount: "المكافأة الشهرية",
      attendanceBonusCriteria: "معايير مكافأة الحضور (%)",
    },

    features: {
      title: "ميزات النظام والخصوصية",
      privateMessaging: "الرسائل الخاصة",
      documentSharing: "مشاركة المستندات",
      performanceReviews: "تقييمات الأداء",
      salarySlips: "قسائم الراتب",
    },

    fingerprint: {
      title: "إعدادات البصمة",
      detectionMode: "وضع كشف الحضور",
      detectionModeDesc: "كيف يحدد النظام ما إذا كان حدث البصمة تسجيل حضور أو انصراف",
      deviceName: "اسم الجهاز",
      deviceNameDesc: "يستخدم اسم جهاز البصمة لتحديد الحضور/الانصراف. يتطلب أجهزة منفصلة لكل منهما.",
      sequential: "تسلسلي",
      sequentialDesc: "يحدد الحضور/الانصراف بناءً على تسلسل الأحداث وتوقيت الوردية. يعمل بجهاز واحد.",
      currentMode: "الوضع الحالي",
    },
  },
};

export default text;